import { useEffect, useState, useCallback, useRef } from 'react';

import type { AvailableSite } from '@atlassian/gic-anywhere';

import { NoNetworkError, causedByAbortError } from '@confluence/network';

import type { JiraProject } from '../__types__/apiUtils';
// TODO: move to __types__/apiUtils.ts or create a new file for single issue create form types
import type { IssueType } from '../providers/BulkCreateContextProvider';

import { fetchIssueTypes } from './apiUtils';

export const FETCH_ISSUE_TYPES = 'fetch-issue-types';

export type UseGetIssueTypesResult = {
	optionsIssueTypes: IssueType[];
	selectedIssueType: IssueType | null;
	isLoading: boolean;
	error: Error | null;
	onIssueTypeChange: (issueType: IssueType | null) => void;
};

export const useGetIssueTypesForSingleIssueCreateForm = (
	selectedAvailableSite: AvailableSite | null = null,
	selectedProject: JiraProject | null = null,
): UseGetIssueTypesResult => {
	const [optionsIssueTypes, setOptionsIssueTypes] = useState<IssueType[]>([]);
	const [selectedIssueType, setSelectedIssueType] = useState<IssueType | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<Error | null>(null);
	const abortControllerRef = useRef<AbortController | null>(null);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
		};
	}, [selectedProject?.id]);

	const resetState = useCallback(() => {
		setOptionsIssueTypes([]);
		setSelectedIssueType(null);
		setError(null);
	}, []);

	// Reset state when project changes
	useEffect(() => {
		resetState();
	}, [selectedProject?.id, resetState]);

	// Fetch issue types when dependencies are available
	useEffect(() => {
		const cloudId = selectedAvailableSite?.cloudId;
		const projectId = selectedProject?.id;

		if (!cloudId || !projectId) {
			return;
		}

		const fetchIssues = async () => {
			// Abort any ongoing requests
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
			// Create new abort controller
			abortControllerRef.current = new AbortController();

			setIsLoading(true);
			setError(null);

			try {
				const issueTypes = await fetchIssueTypes({
					cloudId,
					projectId,
					signal: abortControllerRef.current?.signal,
				});

				// Filter out subtasks
				const validIssueTypes = issueTypes?.filter((it) => !it.subtask) || [];
				setOptionsIssueTypes(validIssueTypes);

				if (validIssueTypes.length > 0) {
					setSelectedIssueType(validIssueTypes[0]);
				}
			} catch (e: Error | unknown) {
				if (e instanceof NoNetworkError && causedByAbortError(e)) {
					resetState();
					return;
				}
				resetState();
				setError(e as Error);
			} finally {
				setIsLoading(false);
			}
		};

		void fetchIssues();
	}, [selectedAvailableSite?.cloudId, selectedProject?.id, resetState]);

	const handleIssueTypeChange = useCallback((issueType: IssueType | null) => {
		setSelectedIssueType(issueType);
	}, []);

	return {
		optionsIssueTypes,
		selectedIssueType,
		isLoading,
		error,
		onIssueTypeChange: handleIssueTypeChange,
	};
};
